'use client';

import { TRACKED_EVENT_NAME, TrackedEventProps } from '@/constants';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';
import { validateGtag } from '@/utils';
import { useLocale } from 'next-intl';
import { ButtonHTMLAttributes, useCallback } from 'react';

export type TrackedButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'ref'
> &
  TrackedEventProps & {
    /** 콜백 ref 도 받는다 — 배열로 여러 버튼을 모아 잡는 호출부가 있다 */
    ref?: React.Ref<HTMLButtonElement>;
    eventName?: (typeof TRACKED_EVENT_NAME)[keyof typeof TRACKED_EVENT_NAME];
    gtagOptions?: Record<string, unknown>;
  };

export function TrackedButton({
  label,
  position,
  children,
  onClick,
  eventName,
  gtagOptions,
  ref,
  ...props
}: TrackedButtonProps) {
  const addGtag = useAddClickButtonGtag({ label, position });
  const locale = useLocale();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (eventName) {
        // Use custom event name and options
        if (validateGtag()) {
          window.gtag('event', eventName, {
            event_label: label,
            position,
            user_language: locale,
            ...gtagOptions,
          });
        }
      } else {
        addGtag();
      }
      onClick?.(e);
    },
    [addGtag, onClick, eventName, gtagOptions, label, position, locale],
  );

  return (
    <button
      ref={ref}
      onClick={handleClick}
      // 프리렌더된 gene 정적 페이지의 위임 리스너용 (tracked-anchor 와 동일 규약)
      data-gtag-event={eventName ?? TRACKED_EVENT_NAME.CLICK_BUTTON}
      data-gtag-label={label}
      data-gtag-position={position}
      data-gtag-extra={gtagOptions ? JSON.stringify(gtagOptions) : undefined}
      {...props}
    >
      {children}
    </button>
  );
}
